import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchX } from 'lucide-react';
import { AudienceLevel, LEVEL_LABELS, MatchedModule } from '../../types/pathway';
import { ModuleSort, SORT_OPTIONS, sortModules } from '../../utils/pathwayMatching';
import { SelectDropdown } from '../SelectDropdown';
import { ModuleCard } from './ModuleCard';
import { PathwayPagination } from './PathwayPagination';
interface ResultsPanelProps {
  terms: string[];
  results: MatchedModule[];
  pathwayIds: string[];
  audience: AudienceLevel | 'any';
  onToggleModule: (moduleId: string) => void;
}
const PAGE_SIZE = 8;
export function ResultsPanel({
  terms,
  results,
  pathwayIds,
  audience,
  onToggleModule
}: ResultsPanelProps) {
  const [sort, setSort] = useState<ModuleSort>('match');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Topics are edited upstream and results re-rank in place, so a change must
  // not leave the rep stranded on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [terms]);
  const visible = useMemo(() => sortModules(results, sort), [results, sort]);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  return <section aria-label="Matching skill modules" data-id="element-1901" data-mp="3lh22pst9amv">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3" data-id="element-1902" data-mp="3oczmd0qxxas">
        <div data-id="element-1903" data-mp="3usl75f5eamsn">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1904" data-mp="3uv7cd84tcq7p">
            {visible.length} matching {visible.length === 1 ? 'module' : 'modules'}
          </h2>
          <p className="mt-1 text-sm text-gray-600" aria-live="polite" data-id="element-1905" data-mp="8mo5ydru1po9">
            {visible.length > 0 && `Showing ${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, visible.length)} of ${visible.length}. `}
            {audience === 'any' ? 'Showing modules across all audiences.' : `Showing modules for ${LEVEL_LABELS[audience].toLowerCase()}.`}
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1906" data-mp="idj7dd08qi27">
          {pageCount > 1 && <span className="text-sm text-gray-500" data-id="element-1907" data-mp="n8ykg2qfc9xk">
              Page {safePage} of {pageCount}
            </span>}
          <SelectDropdown ariaLabel="Sort modules" size="sm" menuAlign="right" value={sort} onChange={value => {
          setSort(value as ModuleSort);
          setPage(1);
        }} options={SORT_OPTIONS.map(option => ({
          value: option.value,
          label: `Sort: ${option.label}`
        }))} data-id="element-1908" data-mp="s79twcct4if9" />
        </div>
      </div>

      {pageItems.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white p-12 text-center shadow-sm" data-id="element-1909" data-mp="1fx42uiu09mu8">
          <SearchX size={28} className="mx-auto text-gray-400" data-id="element-1910" data-mp="1k125v4mgbhsk" />
          <h3 className="mt-3 text-lg font-semibold text-gray-900" data-id="element-1911" data-mp="34jw2o1qkda2h">
            No modules match yet
          </h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-600" data-id="element-1912" data-mp="30fod62ca7q37">
            Try removing a topic or adding a different one.
          </p>
        </div> : <motion.div layout className="space-y-3" data-id="element-1913" data-mp="2qjv3cqpy6ap4">
          {pageItems.map(match => <motion.div key={match.module.id} layout initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-1914" data-mp="3o7bxg1f1g2wi">
              <ModuleCard match={match} inPathway={pathwayIds.includes(match.module.id)} expanded={expandedId === match.module.id} onToggleExpand={() => setExpandedId(expandedId === match.module.id ? null : match.module.id)} onTogglePathway={() => onToggleModule(match.module.id)} data-id="element-1915" data-mp="3eexskzsnyg64" />
            </motion.div>)}
        </motion.div>}

      <PathwayPagination label="Recommended module pages" page={safePage} pageCount={pageCount} onChange={setPage} data-id="element-1916" data-mp="3qnubgc0pc8wr" />
    </section>;
}